import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Check, Star, Sparkles } from "lucide-react";
import SubscriptionService from "../../services/organization/SubscriptionService";

const DEFAULT_PLANS = [
  {
    id: "free_trial",
    name: "Free Trial Plan",
    price: "₹0",
    period: "/14 Days",
    badge: "Free Evaluation",
    description: "14-Day Free Evaluation with 20 export documents and 2 user seats.",
    features: [
      "20 Export Documents",
      "2 Team User Seats",
      "Quotations & Proforma Invoices",
      "Commercial Invoices & Packing Lists",
      "Standard PDF & Excel Generation",
      "14 Days Full System Access",
    ],
    ctaText: "Start 14-Day Free Trial",
    ctaLink: "/signup",
    popular: false,
  },
  {
    id: "growth",
    name: "Growth Plan",
    price: "₹2,999",
    period: "/1 Months",
    badge: "Most Popular",
    description: "Ideal for active ceramic exporters. 100 export documents per month and 5 team seats.",
    features: [
      "100 Export Documents / Month",
      "5 Team User Seats with Permissions",
      "Full Trade Suite (CI, PL, BL Draft, VGM, Annexure)",
      "Custom Document Numbering & Series",
      "Multi-Currency & GST Invoicing",
      "Standard Support via Email & Ticket",
    ],
    ctaText: "Get Started with Growth",
    ctaLink: "/signup",
    popular: true,
  },
  {
    id: "enterprise",
    name: "Enterprise Plan",
    price: "₹7,999",
    period: "/1 Months",
    badge: "Unlimited Scale",
    description: "Unlimited documents, unlimited user seats, and priority customer support.",
    features: [
      "Unlimited Export Documents",
      "Unlimited Team User Seats & Branches",
      "Version History & Full Audit Trail",
      "Advanced Incoterm Pricing & Calculations",
      "Dedicated Account Manager",
      "Priority 24/7 Enterprise Support",
    ],
    ctaText: "Upgrade to Enterprise",
    ctaLink: "/signup",
    popular: false,
  },
];

const getDynamicFeatures = (plan) => {
  const docText = plan.unlimited_documents ? "Unlimited Export Documents" : `${plan.document_limit} Export Documents`;
  const seatText = plan.unlimited_users ? "Unlimited Team User Seats" : `${plan.user_seat_limit} Team User Seats`;

  if (plan.is_free) {
    return [
      docText,
      seatText,
      "Quotations & Proforma Invoices",
      "Commercial Invoices & Packing Lists",
      "Standard PDF & Excel Generation",
      `${plan.duration_formatted} Full System Access`,
    ];
  }

  if (plan.name?.toLowerCase().includes("enterprise")) {
    return [
      docText,
      seatText,
      "Multi-Organization & Multi-Branch Support",
      "Version History & Full Audit Trail",
      "Advanced Incoterm Pricing & Calculations",
      "Priority 24/7 Enterprise Support",
    ];
  }

  return [
    docText,
    seatText,
    "Full Trade Suite (CI, PL, BL Draft, VGM, Annexure)",
    "Custom Document Numbering & Series",
    "Multi-Currency & GST Invoicing",
    "Standard Support via Email & Ticket",
  ];
};

export const PricingSection = () => {
  const [plans, setPlans] = useState(DEFAULT_PLANS);

  useEffect(() => {
    let isMounted = true;

    const fetchPlans = async () => {
      try {
        const response = await SubscriptionService.getSubscriptionPlans();
        const apiPlans = response?.data?.data || response?.data;
        if (Array.isArray(apiPlans) && apiPlans.length > 0 && isMounted) {
          const mapped = apiPlans.map((p) => {
            const isGrowth = p.name?.toLowerCase().includes("growth");
            const isEnterprise = p.name?.toLowerCase().includes("enterprise");
            const priceFormatted = p.is_free
              ? "₹0"
              : `₹${Number(p.price || 0).toLocaleString("en-IN")}`;

            return {
              id: p.id,
              name: p.name,
              price: priceFormatted,
              period: `/${p.duration_formatted || "Month"}`,
              badge: isGrowth ? "Most Popular" : isEnterprise ? "Unlimited Scale" : "Free Evaluation",
              description: p.description,
              features: getDynamicFeatures(p),
              ctaText: p.is_free
                ? "Start Free Trial"
                : isGrowth
                ? "Get Started with Growth"
                : "Upgrade to Enterprise",
              ctaLink: "/signup",
              popular: isGrowth,
            };
          });
          setPlans(mapped);
        }
      } catch (err) {
        // Fallback to default plans seamlessly if backend is unreachable
      }
    };

    fetchPlans();
    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <section id="pricing" className="bg-slate-50 py-20 border-t border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16 space-y-3">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-900 border border-amber-200">
            <Sparkles className="w-3.5 h-3.5 text-amber-700" />
            Live Subscription Plans
          </div>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
            Plans Designed for Export Excellence
          </h2>
          <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto">
            Choose the subscription tier that matches your shipment volume. Synced directly with your workspace quota.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch">
          {plans.map((plan) => (
            <Card
              key={plan.id || plan.name}
              className={`relative flex flex-col justify-between transition-all duration-300 rounded-2xl ${
                plan.popular
                  ? "border-2 border-amber-500 shadow-xl bg-white scale-100 md:scale-105 z-10"
                  : "border border-slate-200/90 shadow-xs bg-white hover:shadow-md"
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-3.5 left-1/2 transform -translate-x-1/2">
                  <div className="bg-amber-600 text-white px-3.5 py-1 rounded-full text-xs font-bold tracking-wide flex items-center gap-1 shadow-sm">
                    <Star className="w-3.5 h-3.5 fill-white" />
                    {plan.badge}
                  </div>
                </div>
              )}
              <CardHeader className="text-center pb-4 pt-6">
                <CardTitle className="text-2xl font-bold text-slate-900">
                  {plan.name}
                </CardTitle>
                <p className="text-xs text-slate-500 mt-1 min-h-[32px]">{plan.description}</p>
                <div className="mt-4 flex items-baseline justify-center gap-1">
                  <span className="text-4xl font-extrabold text-slate-900 tracking-tight">
                    {plan.price}
                  </span>
                  <span className="text-sm font-semibold text-slate-500">{plan.period}</span>
                </div>
              </CardHeader>
              <CardContent className="pt-2 flex-1 flex flex-col justify-between">
                <div className="border-t border-slate-100 pt-5 pb-6">
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">
                    Plan Highlights
                  </p>
                  <ul className="space-y-3">
                    {plan.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start gap-2.5 text-xs text-slate-700">
                        <Check className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <Link
                  to={plan.ctaLink}
                  className={`w-full py-2.5 px-4 rounded-xl text-xs font-bold text-center block transition-all shadow-xs ${
                    plan.popular
                      ? "bg-amber-600 hover:bg-amber-700 text-white hover:shadow-md"
                      : "bg-slate-900 hover:bg-slate-800 text-white"
                  }`}
                >
                  {plan.ctaText}
                </Link>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
